'use client';

import React, { useState } from 'react';
import { XMarkIcon, SparklesIcon, ClockIcon, CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { createClient } from '@/lib/local-api/client';

const ADMIN_WALLET = 'TYourAdminUSDTWalletAddressHere'; // Replace with actual USDT wallet

const TIERS = [
  {
    key: 'standard',
    label: 'Standard',
    description: 'Boosted in feed, slightly higher visibility',
    color: 'border-blue-500/40 bg-blue-500/5',
    badge: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    pricePerDay: 2,
    icon: '🚀',
  },
  {
    key: 'premium',
    label: 'Premium',
    description: 'Pinned at top of feed, highlighted border',
    color: 'border-amber-500/40 bg-amber-500/5',
    badge: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    pricePerDay: 5,
    icon: '⭐',
  },
  {
    key: 'elite',
    label: 'Elite',
    description: 'Featured spotlight, shown to all users',
    color: 'border-purple-500/40 bg-purple-500/5',
    badge: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
    pricePerDay: 10,
    icon: '👑',
  },
];

const DURATIONS = [
  { days: 3, label: '3 Days' },
  { days: 7, label: '7 Days' },
  { days: 14, label: '14 Days' },
  { days: 30, label: '30 Days' },
];

interface BoostPostModalProps {
  postId?: string;
  projectId?: string;
  projectName?: string;
  sourceType: 'connect_post' | 'project_review';
  onClose: () => void;
}

export default function BoostPostModal({ postId, projectId, projectName, sourceType, onClose }: BoostPostModalProps) {
  const [tier, setTier] = useState('standard');
  const [duration, setDuration] = useState(7);
  const [txHash, setTxHash] = useState('');
  const [step, setStep] = useState<'configure' | 'payment' | 'submitted'>('configure');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectedTier = TIERS.find((t) => t.key === tier)!;
  const totalUsdt = selectedTier.pricePerDay * duration;

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(ADMIN_WALLET).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = async () => {
    if (!txHash.trim()) return;
    setSubmitting(true);
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSubmitting(false); return; }

    const payload: Record<string, unknown> = {
      user_id: user.id,
      source_type: sourceType,
      duration_days: duration,
      visibility_tier: tier,
      amount_usdt: totalUsdt,
      wallet_address: ADMIN_WALLET,
      tx_hash: txHash.trim(),
      status: 'pending_review',
    };
    if (postId) payload.post_id = postId;
    if (projectId) payload.project_id = projectId;
    if (projectName) payload.project_name = projectName;

    const { error } = await supabase.from('boosted_posts').insert(payload);
    setSubmitting(false);
    if (!error) setStep('submitted');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
              <SparklesIcon style={{ width: 16, height: 16 }} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Boost This Post</h2>
              <p className="text-xs text-muted-foreground">
                {sourceType === 'connect_post' ? 'Community Post' : `Project: ${projectName}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Step: Configure */}
        {step === 'configure' && (
          <div className="p-5 space-y-5">
            {/* Tier Selection */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">Visibility Tier</p>
              <div className="space-y-2">
                {TIERS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTier(t.key)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                      tier === t.key ? t.color + ' border-opacity-100' : 'border-border bg-muted/30 hover:border-border/80'
                    }`}
                  >
                    <span className="text-xl leading-none">{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-foreground">{t.label}</span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${t.badge}`}>
                          ${t.pricePerDay}/day
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                    </div>
                    {tier === t.key && (
                      <CheckCircleIcon style={{ width: 16, height: 16 }} className="text-primary flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration Selection */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">Duration</p>
              <div className="grid grid-cols-4 gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d.days}
                    onClick={() => setDuration(d.days)}
                    className={`py-2 rounded-lg text-xs font-semibold border transition-all ${
                      duration === d.days
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-muted/50 rounded-xl p-3.5 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ClockIcon style={{ width: 14, height: 14 }} />
                <span>{duration} days · {selectedTier.label}</span>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-foreground font-mono">${totalUsdt} USDT</p>
                <p className="text-[10px] text-muted-foreground">Total cost</p>
              </div>
            </div>

            <button
              onClick={() => setStep('payment')}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 active:scale-95 transition-all"
            >
              Continue to Payment →
            </button>
          </div>
        )}

        {/* Step: Payment */}
        {step === 'payment' && (
          <div className="p-5 space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-3.5 text-sm text-amber-600 dark:text-amber-400">
              <p className="font-semibold mb-1">Send exactly <span className="font-mono">${totalUsdt} USDT</span> (TRC-20)</p>
              <p className="text-xs opacity-80">to the wallet address below, then paste your transaction hash.</p>
            </div>

            {/* Wallet address */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Admin Wallet (TRC-20 USDT)</p>
              <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2.5 border border-border">
                <code className="flex-1 text-xs text-foreground font-mono break-all">{ADMIN_WALLET}</code>
                <button
                  onClick={handleCopyWallet}
                  className="flex-shrink-0 p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copied
                    ? <CheckCircleIcon style={{ width: 14, height: 14 }} className="text-positive" />
                    : <ClipboardDocumentIcon style={{ width: 14, height: 14 }} />
                  }
                </button>
              </div>
            </div>

            {/* TX Hash input */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Transaction Hash</p>
              <input
                type="text"
                value={txHash}
                onChange={(e) => setTxHash(e.target.value)}
                placeholder="Paste your TxID / transaction hash here"
                className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring font-mono"
              />
            </div>

            {/* Summary recap */}
            <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/40 rounded-xl px-3 py-2.5">
              <span>{selectedTier.icon} {selectedTier.label} · {duration} days</span>
              <span className="font-mono font-bold text-foreground">${totalUsdt} USDT</span>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStep('configure')}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!txHash.trim() || submitting}
                className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit Boost Request'}
              </button>
            </div>
          </div>
        )}

        {/* Step: Submitted */}
        {step === 'submitted' && (
          <div className="p-8 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-positive/10 flex items-center justify-center">
              <CheckCircleIcon style={{ width: 32, height: 32 }} className="text-positive" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Boost Request Submitted!</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Our admin team will verify your payment and activate the boost within 24 hours.
              </p>
            </div>
            <div className="w-full bg-muted/50 rounded-xl p-3 text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between">
                <span>Tier</span>
                <span className="font-semibold text-foreground">{selectedTier.icon} {selectedTier.label}</span>
              </div>
              <div className="flex justify-between">
                <span>Duration</span>
                <span className="font-semibold text-foreground">{duration} days</span>
              </div>
              <div className="flex justify-between">
                <span>Amount</span>
                <span className="font-mono font-bold text-foreground">${totalUsdt} USDT</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition-all"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
